/* Copyright (c) 2025 MOCA Systems, Inc.  All rights reserved. */

import * as appStrings from '@touchplan/strings';
import { ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NonNullableFormBuilder, ReactiveFormsModule, ValidatorFn, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { BehaviorSubject, Subject } from 'rxjs';
import { MaterialModule } from 'src/app/common/material/material-module';
import { PlanPrintService } from 'src/app/modules/plan/services/plan-print.service';
import { debounceTime, filter, takeUntil } from 'rxjs/operators';
import {
	DPI_OPTIONS,
	IPlanPrintSettingsForm,
	MIN_HEIGHT_INCHES,
	MIN_WIDTH_INCHES,
	ORIENTATION_OPTIONS,
	PAGE_SIZE_CUSTOM,
	PAGE_SIZE_OPTIONS,
	PLAN_PRINT_SETTINGS_DEFAULTS,
	ROLE_LEGEND_OPTIONS,
	TEXT_SIZE_OPTIONS,
	UNIT_OPTIONS,
} from './plan-print-settings.model';
import {
	defaultPrintConfiguration,
	IPrintConfiguration,
	PaperOrientations,
	PrintActions,
	PrintTextSizes,
	RoleLegendOptions,
} from 'src/app/app-data/constants/plan-print.model';
import { PaperTypes, PaperUnits } from '../../app-data/constants/paper-sizes';
import { SharedUtilityComponentsModule } from '../../common/utility-components/shared-utility-components.module';
import { BannerMessageHandler } from '../../common/logging/banner-message-handler';
import {
	PlanPrintJobOrchestratorService,
	PrintJobResult,
	PrintJobStatus,
} from './plan-print-job-orchestrator.service';

@Component({
	selector: 'plan-print-settings',
	standalone: true,
	imports: [CommonModule, ReactiveFormsModule, MaterialModule, SharedUtilityComponentsModule],
	templateUrl: './plan-print-settings.component.html',
	styleUrls: ['./plan-print-settings.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PlanPrintSettingsComponent implements OnInit, OnDestroy {
	private readonly activatedRoute = inject(ActivatedRoute);
	private readonly bannerMessageHandler = inject(BannerMessageHandler);
	private readonly formBuilder = inject(NonNullableFormBuilder);
	private readonly planPrintService = inject(PlanPrintService);
	private readonly printJobOrchestrator = inject(PlanPrintJobOrchestratorService);
	private readonly shutdownSubject$ = new Subject<void>();
	private readonly projectId: string | null = null;

	public appStrings = appStrings;
	public orientationOptions = ORIENTATION_OPTIONS;
	public roleLegendOptions = ROLE_LEGEND_OPTIONS;
	public textSizeOptions = TEXT_SIZE_OPTIONS;
	public unitsOptions = UNIT_OPTIONS;
	public dpiOptions = DPI_OPTIONS;
	public pageSizeOptions = PAGE_SIZE_OPTIONS;
	public isPrinting$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

	public formGroup: IPlanPrintSettingsForm;

	constructor() {
		this.formGroup = this.createFormGroup();
		this.projectId = this.activatedRoute.snapshot.paramMap.get('projectId');
	}

	public ngOnInit() {
		// listen to form changes
		this.handleFormValueChanges();

		// react to page size selection changes
		this.handlePrintSizeSelectionChanges();

		// set the defaults
		this.resetFormToDefaults();
	}

	public ngOnDestroy(): void {
		this.shutdownSubject$.next();
		this.shutdownSubject$.complete();
		this.isPrinting$.complete();
	}

	private createFormGroup(): IPlanPrintSettingsForm {
		return this.formBuilder.group({
			orientation: this.formBuilder.control<PaperOrientations | null>(null),
			roleLegend: this.formBuilder.control<RoleLegendOptions | null>(null),
			textSize: this.formBuilder.control<PrintTextSizes | null>(null),
			units: this.formBuilder.control<PaperUnits | null>({ value: null, disabled: true }),
			dpi: this.formBuilder.control<number | null>(null),
			pageSize: this.formBuilder.control<PaperTypes | null>(null),
			width: this.formBuilder.control<number | null>({ value: null, disabled: true }),
			height: this.formBuilder.control<number | null>({ value: null, disabled: true }),
		});
	}

	/**
	 * Subscribes to changes in the entire form and debounces them before broadcasting updated print settings.
	 * Emits only when the form is valid.
	 */
	private handleFormValueChanges(): void {
		this.formGroup.valueChanges
			.pipe(
				debounceTime(500),
				filter(() => this.formGroup.valid),
				takeUntil(this.shutdownSubject$)
			)
			.subscribe(() => this.onPrintSettingsChange());
	}

	/**
	 * Reacts to changes in the selected page size.
	 * If a predefined size is selected, disables width/height/unit inputs.
	 * If 'Custom' is selected, enables these fields.
	 */
	private handlePrintSizeSelectionChanges(): void {
		this.pageSize.valueChanges.pipe(takeUntil(this.shutdownSubject$)).subscribe((key) => {
			const isCustom = key === PAGE_SIZE_CUSTOM;
			if (isCustom) {
				this.widthControl.enable();
				this.heightControl.enable();
				this.unitsControl.enable();

				this.widthControl.setValidators([Validators.required, Validators.min(MIN_WIDTH_INCHES)]);
				this.heightControl.setValidators([Validators.required, Validators.min(MIN_HEIGHT_INCHES)]);
			} else {
				const selected = PAGE_SIZE_OPTIONS.find((p) => p.key === key);
				this.widthControl.setValue(selected?.paperSize?.width ?? 0);
				this.heightControl.setValue(selected?.paperSize?.height ?? 0);
				this.unitsControl.setValue(selected?.paperSize?.units ?? PaperUnits.Inches);

				this.widthControl.disable();
				this.heightControl.disable();
				this.unitsControl.disable();

				this.widthControl.clearValidators();
				this.heightControl.clearValidators();
			}

			this.widthControl.updateValueAndValidity();
			this.heightControl.updateValueAndValidity();
			this.unitsControl.updateValueAndValidity();
		});
	}

	/**
	 * Applies the default print settings to the form.
	 * Triggers the form change subscription by emitting the value.
	 */
	private resetFormToDefaults(): void {
		this.planPrintService.switchViewToEdit();
		this.formGroup.setValue(PLAN_PRINT_SETTINGS_DEFAULTS, { emitEvent: true });
	}

	/**
	 * Broadcasts the message that the print button was clicked and print can be started.
	 */
	public onPrintClick(): void {
		if (!this.formGroup.valid || !this.projectId) return;

		this.isPrinting$.next(true);

		this.printJobOrchestrator
			.captureConvertUpload(this.projectId)
			.pipe(takeUntil(this.shutdownSubject$))
			.subscribe({
				next: (p: PrintJobResult) => {
					if (p.step === PrintJobStatus.Failed && p.message) {
						this.bannerMessageHandler.displayErrorMessage(
							new Error(this.appStrings.PLAN_PRINT_CAPTURE_UPLOAD_ERROR)
						);
					}
				},
				complete: () => {
					this.isPrinting$.next(false);
				},
			});
	}

	/**
	 * Broadcasts the latest valid plan print settings.
	 * Transforming the valid form values into a complete, non-nullable {@link IPrintConfiguration} object.
	 * nullable form controls for flexibility in UX but the downstream consumer requires strictly non-nullable
	 * object.
	 *
	 * This method is only called when the form is valid, so all required fields are assumed
	 * to be present and non-null. It uses {@link getRawValue} to extract disabled fields (e.g., width/height),
	 * and supplements the configuration with default values for non-user-editable fields.
	 *
	 * The result is broadcast to the {@link PlanPrintService}.
	 */
	private onPrintSettingsChange(): void {
		const { pageSize, width, height, units, orientation, dpi, textSize, roleLegend } =
			this.formGroup.getRawValue();
		const config: IPrintConfiguration = {
			pageSize,
			width,
			height,
			units,
			orientation,
			dpi,
			textSize,
			roleLegend,
			includeProjectLogo: defaultPrintConfiguration.includeProjectLogo,
			scope: defaultPrintConfiguration.scope,
			action: PrintActions.Edit,
		} as IPrintConfiguration;
		this.planPrintService.setConfig(config);
	}

	protected getInvalidMinWidthMessage(): string {
		const selectedUnits = this.unitsControl.value;
		return `${appStrings.PLAN_PRINT_INVALID_WIDTH} (min ${MIN_WIDTH_INCHES}x${MIN_HEIGHT_INCHES} ${selectedUnits})`;
	}

	protected getInvalidMinHeightMessage(): string {
		const selectedUnits = this.unitsControl.value;
		return `${appStrings.PLAN_PRINT_INVALID_HEIGHT} (min ${MIN_WIDTH_INCHES}x${MIN_HEIGHT_INCHES} ${selectedUnits})`;
	}

	private get widthControl() {
		return this.formGroup.controls.width;
	}

	private get heightControl() {
		return this.formGroup.controls.height;
	}

	private get unitsControl() {
		return this.formGroup.controls.units;
	}

	private get dpiControl() {
		return this.formGroup.controls.dpi;
	}

	private get pageSize() {
		return this.formGroup.controls.pageSize;
	}
}
